import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString('fr-FR');
}

function docStatut(date_fin) {
  if (!date_fin) return { label: 'Permanent', style: { background: '#e8f4fd', color: '#1a6fa0' } };
  const fin = new Date(date_fin);
  const now = new Date();
  const diff = Math.ceil((fin - now) / (1000 * 60 * 60 * 24));
  if (diff < 0)  return { label: 'Expiré',        style: { background: '#fdecea', color: '#c0392b' } };
  if (diff <= 30) return { label: `Expire dans ${diff}j`, style: { background: '#fff3cd', color: '#856404' } };
  return { label: 'Valide', style: { background: '#d4edda', color: '#155724' } };
}

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

const CONFIANCE_STYLE = {
  haute:   { color: '#155724', background: '#d4edda', label: '✓ Haute confiance' },
  moyenne: { color: '#856404', background: '#fff3cd', label: '~ Confiance moyenne' },
  faible:  { color: '#721c24', background: '#f8d7da', label: '⚠ Faible confiance' },
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const S = {
  section: { marginBottom: '2rem' },
  sectionHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: '1rem', paddingBottom: '0.6rem', borderBottom: '2px solid #ecf0f1',
  },
  sectionTitle: { fontSize: '1rem', fontWeight: 700, color: '#2c3e50', margin: 0 },

  dropZone: (active) => ({
    border: `2px dashed ${active ? '#3498db' : '#bdc3c7'}`,
    borderRadius: '10px',
    padding: '2.5rem 1.5rem',
    textAlign: 'center',
    background: active ? '#eaf4fd' : '#fafbfc',
    cursor: 'pointer',
    transition: 'all 0.2s',
    marginBottom: '1.5rem',
  }),
  dropIcon: { fontSize: '2.5rem', marginBottom: '0.75rem' },
  dropText: { color: '#7f8c8d', fontSize: '0.9rem', lineHeight: 1.6 },
  dropBold: { color: '#2c3e50', fontWeight: 600 },

  analyseCard: {
    background: '#fff', border: '1px solid #e8ecef', borderRadius: '8px',
    marginBottom: '1rem', overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },
  analyseHeader: {
    display: 'flex', alignItems: 'center', gap: '0.75rem',
    padding: '0.85rem 1.1rem', background: '#f8f9fa',
    borderBottom: '1px solid #e8ecef',
  },
  analyseBody: { padding: '1rem 1.1rem' },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' },
  field: { display: 'flex', flexDirection: 'column', gap: '0.25rem' },
  fieldLabel: { fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#95a5a6' },
  fieldInput: {
    padding: '0.5rem 0.65rem', border: '1px solid #ddd', borderRadius: '4px',
    fontSize: '0.88rem', width: '100%', boxSizing: 'border-box',
  },
  analyseFooter: {
    display: 'flex', justifyContent: 'flex-end', gap: '0.6rem',
    padding: '0.75rem 1.1rem', borderTop: '1px solid #f0f0f0', background: '#fafafa',
  },
  btnSmall: (color) => ({
    padding: '0.4rem 0.9rem', borderRadius: '4px', fontSize: '0.82rem',
    fontWeight: 600, cursor: 'pointer', border: 'none',
    background: color === 'primary' ? '#3498db' : color === 'success' ? '#27ae60' : color === 'danger' ? '#e74c3c' : '#ecf0f1',
    color: color === 'light' ? '#2c3e50' : '#fff',
  }),

  spinner: {
    display: 'inline-block', width: '16px', height: '16px',
    border: '2px solid rgba(52,152,219,0.25)', borderTopColor: '#3498db',
    borderRadius: '50%', animation: 'spin 0.7s linear infinite', flexShrink: 0,
  },

  badge: (style) => ({
    ...style, padding: '0.2rem 0.55rem', borderRadius: '10px',
    fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap',
  }),
  fileIcon: { fontSize: '1.1rem', marginRight: '0.4rem' },
};

// ─── Composant Champ Éditable ────────────────────────────────────────────────
function EditableField({ label, value, field, disabled, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || '');

  useEffect(() => {
    setVal(value || '');
  }, [value]);

  const handleSave = async () => {
    try {
      await onSave(field, val);
      setEditing(false);
    } catch(err) {
      alert('Erreur lors de la sauvegarde');
    }
  };

  const handleCancel = () => {
    setVal(value || '');
    setEditing(false);
  };

  return (
    <div className="detail-item">
      <div className="detail-label">{label}</div>
      <div className="detail-value" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        {editing ? (
          <>
            <input
              type="text"
              value={val}
              onChange={e => setVal(e.target.value)}
              style={{
                padding: '0.5rem 0.7rem',
                border: '2px solid #3498db',
                borderRadius: '4px',
                fontSize: '0.9rem',
                flex: 1,
                outline: 'none'
              }}
              autoFocus
              onKeyDown={e => {
                if (e.key === 'Enter') handleSave();
                if (e.key === 'Escape') handleCancel();
              }}
            />
            <button
              onClick={handleSave}
              style={{
                background: '#27ae60',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                padding: '0.4rem 0.7rem',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: 600
              }}
              title="Enregistrer (Enter)"
            >
              ✓
            </button>
            <button
              onClick={handleCancel}
              style={{
                background: '#e74c3c',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                padding: '0.4rem 0.7rem',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: 600
              }}
              title="Annuler (Esc)"
            >
              ✕
            </button>
          </>
        ) : (
          <>
            <span style={{ flex: 1 }}>{value || '—'}</span>
            {!disabled && (
              <button
                onClick={() => setEditing(true)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.2rem 0.4rem',
                  transition: 'all 0.2s',
                  opacity: 0.4,
                  display: 'flex',
                  alignItems: 'center'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.opacity = '1';
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.opacity = '0.4';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                title="Modifier ce champ"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#95a5a6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Composant ActivitesField (multi-sélection des activités du sous-traitant) ─
function ActivitesField({ activites, typesActivite, onSave }) {
  const [editing, setEditing] = useState(false);
  const [selection, setSelection] = useState([]); // [{type_activite_id, principal}]

  useEffect(() => {
    setSelection((activites || []).map(a => ({ type_activite_id: a.type_activite_id, principal: !!a.principal })));
  }, [activites]);

  const isChecked = (id) => selection.some(s => s.type_activite_id === id);
  const isPrincipal = (id) => selection.some(s => s.type_activite_id === id && s.principal);

  const toggle = (id) => {
    setSelection(prev => {
      if (prev.some(s => s.type_activite_id === id)) {
        return prev.filter(s => s.type_activite_id !== id);
      }
      const hasPrincipal = prev.some(s => s.principal);
      return [...prev, { type_activite_id: id, principal: !hasPrincipal }];
    });
  };

  const setPrincipal = (id) => {
    setSelection(prev => prev.map(s => ({ ...s, principal: s.type_activite_id === id })));
  };

  const handleSave = async () => {
    try {
      await onSave(selection);
      setEditing(false);
    } catch (err) {
      alert('Erreur lors de la sauvegarde des activités');
    }
  };

  const handleCancel = () => {
    setSelection((activites || []).map(a => ({ type_activite_id: a.type_activite_id, principal: !!a.principal })));
    setEditing(false);
  };

  return (
    <div className="detail-item" style={{ gridColumn: 'span 2' }}>
      <div className="detail-label">Activités</div>
      <div className="detail-value" style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
        {editing ? (
          <div style={{ flex: 1 }}>
            <div style={{
              maxHeight: '220px', overflowY: 'auto', border: '1px solid #ddd',
              borderRadius: '4px', padding: '0.5rem 0.75rem', background: '#fff',
            }}>
              {typesActivite.length === 0 ? (
                <div style={{ color: '#95a5a6', fontSize: '0.85rem' }}>Aucun type d'activité configuré</div>
              ) : typesActivite.map(t => (
                <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.25rem 0' }}>
                  <input type="checkbox" checked={isChecked(t.id)} onChange={() => toggle(t.id)} id={`act-${t.id}`} />
                  <label htmlFor={`act-${t.id}`} style={{ flex: 1, fontSize: '0.88rem', cursor: 'pointer' }}>{t.nom}</label>
                  {isChecked(t.id) && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.78rem', color: '#7f8c8d', cursor: 'pointer' }}>
                      <input type="radio" name="activite-principale" checked={isPrincipal(t.id)} onChange={() => setPrincipal(t.id)} />
                      principale
                    </label>
                  )}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
              <button onClick={handleSave} style={{ background: '#27ae60', color: '#fff', border: 'none', borderRadius: '4px', padding: '0.4rem 0.7rem', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>✓ Enregistrer</button>
              <button onClick={handleCancel} style={{ background: '#e74c3c', color: '#fff', border: 'none', borderRadius: '4px', padding: '0.4rem 0.7rem', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>✕ Annuler</button>
            </div>
          </div>
        ) : (
          <>
            <span style={{ flex: 1 }}>
              {(activites || []).length === 0 ? '—' : (activites || []).map((a, i) => (
                <span key={a.type_activite_id} style={{
                  display: 'inline-block', marginRight: '0.4rem', marginBottom: '0.2rem',
                  padding: '0.15rem 0.55rem', borderRadius: '10px', fontSize: '0.8rem',
                  background: a.principal ? '#d4edda' : '#ecf0f1',
                  color: a.principal ? '#155724' : '#34495e',
                  fontWeight: a.principal ? 600 : 400,
                }}>
                  {a.nom}{a.principal ? ' ★' : ''}
                </span>
              ))}
            </span>
            <button
              onClick={() => setEditing(true)}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '0.2rem 0.4rem', opacity: 0.4, display: 'flex', alignItems: 'center' }}
              onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
              onMouseLeave={e => { e.currentTarget.style.opacity = '0.4'; }}
              title="Modifier les activités"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#95a5a6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
              </svg>
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Composant SpinnerIA ──────────────────────────────────────────────────────
function SpinnerIA({ nom }) {
  return (
    <div style={S.analyseCard}>
      <div style={S.analyseHeader}>
        <span style={S.spinner} />
        <span style={{ fontSize: '0.85rem', color: '#3498db', fontWeight: 600 }}>
          Analyse IA en cours…
        </span>
        <span style={{ fontSize: '0.8rem', color: '#95a5a6', marginLeft: 'auto' }}>{nom}</span>
      </div>
      <div style={{ padding: '1rem 1.1rem', color: '#95a5a6', fontSize: '0.82rem' }}>
        Claude analyse le document pour identifier son type et sa date de validité…
      </div>
    </div>
  );
}

// ─── Composant CarteAnalyse ───────────────────────────────────────────────────
function CarteAnalyse({ item, onConfirm, onDiscard, typesDocuments = [] }) {
  // --- Calcul initial synchrone (pas de useEffect) ---
  const initValues = () => {
    const analyse = item.analyse || {};
    let initialTypeId = '';
    let initialType = analyse.type_document || '';
    let initialDate = analyse.date_fin_validite || '';

    // 1) Si le backend a renvoye un type_document_id, l'utiliser
    if (analyse.type_document_id) {
      initialTypeId = String(analyse.type_document_id);
      const found = typesDocuments.find(t => String(t.id) === initialTypeId);
      if (found) initialType = found.type;
    }
    // 2) Sinon matcher par nom (exact puis partiel)
    else if (initialType && typesDocuments.length > 0) {
      const typeIA = initialType.toLowerCase().trim();
      let match = typesDocuments.find(t => t.type.toLowerCase().trim() === typeIA);
      if (!match) {
        match = typesDocuments.find(t => 
          typeIA.includes(t.type.toLowerCase().trim()) || 
          t.type.toLowerCase().trim().includes(typeIA)
        );
      }
      if (match) {
        initialTypeId = String(match.id);
        initialType = match.type;
      }
    }

    // 3) Si pas de date et qu'on a un type avec duree, calculer
    if (!initialDate && initialTypeId && typesDocuments.length > 0) {
      const typeObj = typesDocuments.find(t => String(t.id) === initialTypeId);
      if (typeObj && typeObj.duree_validite_mois) {
        const dateDoc = analyse.date_document;
        const baseDate = dateDoc ? new Date(dateDoc) : new Date();
        if (!isNaN(baseDate.getTime())) {
          baseDate.setMonth(baseDate.getMonth() + typeObj.duree_validite_mois);
          initialDate = baseDate.toISOString().split('T')[0];
        }
      }
    }

    return { initialTypeId, initialType, initialDate };
  };

  const { initialTypeId, initialType, initialDate } = initValues();
  const [typeId, setTypeId] = useState(initialTypeId);
  const [type, setType]     = useState(initialType);
  const [date, setDate]     = useState(initialDate);
  const [saving, setSaving] = useState(false);

  // Si typesDocuments arrive apres le premier rendu, re-matcher
  useEffect(() => {
    if (typesDocuments.length > 0 && !typeId) {
      const analyse = item.analyse || {};
      if (analyse.type_document_id) {
        const tid = String(analyse.type_document_id);
        setTypeId(tid);
        const found = typesDocuments.find(t => String(t.id) === tid);
        if (found) setType(found.type);
      } else if (analyse.type_document) {
        const typeIA = analyse.type_document.toLowerCase().trim();
        let match = typesDocuments.find(t => t.type.toLowerCase().trim() === typeIA);
        if (!match) {
          match = typesDocuments.find(t => 
            typeIA.includes(t.type.toLowerCase().trim()) || 
            t.type.toLowerCase().trim().includes(typeIA)
          );
        }
        if (match) {
          setTypeId(String(match.id));
          setType(match.type);
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typesDocuments]);

  // Recalculer la date si typeId change et pas de date
  useEffect(() => {
    if (typeId && !date && typesDocuments.length > 0) {
      const typeObj = typesDocuments.find(t => String(t.id) === typeId);
      if (typeObj && typeObj.duree_validite_mois) {
        const dateDoc = item.analyse?.date_document;
        const baseDate = dateDoc ? new Date(dateDoc) : new Date();
        if (!isNaN(baseDate.getTime())) {
          baseDate.setMonth(baseDate.getMonth() + typeObj.duree_validite_mois);
          setDate(baseDate.toISOString().split('T')[0]);
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeId, typesDocuments]);

  const handleTypeChange = (e) => {
    const id = e.target.value;
    setTypeId(id);
    const found = typesDocuments.find(t => String(t.id) === id);
    if (found) {
      setType(found.type);
      // Recalculer la date : date du document + duree de validite
      if (found.duree_validite_mois) {
        const dateDoc = item.analyse?.date_document;
        const baseDate = dateDoc ? new Date(dateDoc) : new Date();
        if (!isNaN(baseDate.getTime())) {
          baseDate.setMonth(baseDate.getMonth() + found.duree_validite_mois);
          setDate(baseDate.toISOString().split('T')[0]);
        }
      } else {
        setDate('');
      }
    } else {
      setType('');
      setDate('');
    }
  };

  const confiance = item.analyse?.confiance || 'faible';
  const cStyle    = CONFIANCE_STYLE[confiance] || CONFIANCE_STYLE.faible;

  const fileExt = item.fichier?.nom?.split('.').pop()?.toLowerCase();
  const isPdf   = fileExt === 'pdf';

  return (
    <div style={S.analyseCard}>
      <div style={S.analyseHeader}>
        <span style={{ fontSize: '1.2rem' }}>{isPdf ? '📄' : '🖼️'}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#2c3e50' }}>
            {item.fichier?.nom}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#95a5a6' }}>
            {formatSize(item.fichier?.taille || 0)}
          </div>
        </div>
        <span style={{ ...S.badge(cStyle), fontSize: '0.72rem' }}>
          {cStyle.label}
        </span>
      </div>

      {/* Debug: afficher les donnees brutes de l'analyse */}
      <div style={{ padding: '0.4rem 1.1rem', background: '#fff3e0', borderBottom: '1px solid #ffe0b2',
        fontSize: '0.7rem', color: '#e65100', fontFamily: 'monospace' }}>
        🔍 DEBUG — type_document_id: {JSON.stringify(item.analyse?.type_document_id)} | 
        type_document: "{item.analyse?.type_document}" | 
        date_document: {item.analyse?.date_document || 'null'} | 
        date_fin_validite: {item.analyse?.date_fin_validite || 'null'} | 
        typeId state: "{typeId}" | date state: "{date}"
      </div>

      {item.analyse?.note && (
        <div style={{ padding: '0.6rem 1.1rem', background: '#f0f7ff', borderBottom: '1px solid #e8ecef',
          fontSize: '0.8rem', color: '#1a6fa0', display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
          <span>🤖</span>
          <span>{item.analyse.note}</span>
        </div>
      )}

      <div style={S.analyseBody}>
        <div style={S.grid2}>
          <div style={S.field}>
            <span style={S.fieldLabel}>Type de document</span>
            <select style={S.fieldInput} value={typeId} onChange={handleTypeChange}>
              <option value="">— Sélectionner un type —</option>
              {typesDocuments.map(t => (
                <option key={t.id} value={String(t.id)}>{t.type}{t.obligatoire ? ' *' : ''}</option>
              ))}
            </select>
          </div>
          <div style={S.field}>
            <span style={S.fieldLabel}>Date de fin de validité</span>
            <input style={S.fieldInput} type="date" value={date}
              onChange={e => setDate(e.target.value)} />
          </div>
        </div>
      </div>

      <div style={S.analyseFooter}>
        <button style={S.btnSmall('light')} onClick={() => onDiscard(item.id)}>
          ✕ Annuler
        </button>
        <button style={S.btnSmall('success')} disabled={saving}
          onClick={async () => {
            setSaving(true);
            await onConfirm(item, type, date, typeId);
            setSaving(false);
          }}>
          {saving ? 'Enregistrement…' : '✓ Confirmer & enregistrer'}
        </button>
      </div>
    </div>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────────
function SoustraitantDetail() {
  const { codification } = useParams();
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);

  const [queue, setQueue]   = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const counterRef   = useRef(0);
  const [fichiersRecus, setFichiersRecus] = useState([]);
  const [analyseEnCours, setAnalyseEnCours] = useState({});
  const [typesDocuments, setTypesDocuments] = useState([]);
  const [typesActivite, setTypesActivite] = useState([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchDetail(); fetchFichiersRecus(); fetchTypesDocuments(); fetchTypesActivite(); }, [codification]);

  const fetchTypesDocuments = async () => {
    try {
      const r = await axios.get('/api/documents/types');
      setTypesDocuments(r.data);
    } catch(e) { console.error('Erreur types documents:', e); }
  };

  const fetchTypesActivite = async () => {
    try {
      const r = await axios.get('/api/types-activite');
      setTypesActivite(r.data);
    } catch(e) { console.error('Erreur types activite:', e); }
  };

  const fetchDetail = async () => {
    try {
      const r = await axios.get(`/api/soustraitants/${codification}`);
      setData(r.data);
      setDocuments(r.data.documents || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchDocuments = async () => {
    try {
      const r = await axios.get(`/api/documents/${codification}`);
      setDocuments(r.data);
    } catch(e) { console.error(e); }
  };

  // ── Sauvegarde d'un champ édité ──────────────────────────────────────────────
  const handleFieldSave = async (field, value) => {
    try {
      await axios.put(`/api/soustraitants/${codification}`, {
        [field]: value || null
      });
      // Rafraîchir les données
      await fetchDetail();
    } catch(err) {
      throw new Error(err.response?.data?.error || 'Erreur de sauvegarde');
    }
  };

  // ── Sauvegarde des activités (multi-sélection) ──────────────────────────────
  const handleActivitesSave = async (activites) => {
    try {
      await axios.put(`/api/soustraitants/${codification}`, { activites });
      await fetchDetail();
    } catch(err) {
      throw new Error(err.response?.data?.error || 'Erreur de sauvegarde');
    }
  };

  // ── Traitement des fichiers déposés ──────────────────────────────────────────
  const processFiles = useCallback(async (files) => {
    const fileList = Array.from(files).filter(f => {
      const ext = f.name.split('.').pop().toLowerCase();
      return ['pdf','jpg','jpeg','png','webp'].includes(ext);
    });

    if (!fileList.length) return;

    const newItems = fileList.map(f => ({
      id: ++counterRef.current,
      nom: f.name,
      file: f,
      statut: 'loading',
      analyse: null,
      fichier: null,
    }));

    setQueue(prev => [...prev, ...newItems]);

    newItems.forEach(async (item) => {
      try {
        const fd = new FormData();
        fd.append('file', item.file);
        fd.append('soustraitant_codification', codification);

        const r = await axios.post('/api/documents/analyse', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 180000
        });

        setQueue(prev => prev.map(q =>
          q.id === item.id
            ? { ...q, statut: 'ready', analyse: r.data, fichier: r.data.fichier }
            : q
        ));
      } catch(err) {
        console.error('Erreur analyse:', err);
        const errMsg = err.code === 'ECONNABORTED' 
          ? 'Timeout - le fichier est peut-être trop volumineux ou l\'API est surchargée'
          : err.response?.data?.error || err.message || 'Erreur d\'analyse';
        setQueue(prev => prev.map(q =>
          q.id === item.id
            ? { ...q, statut: 'error', analyse: { note: errMsg, confiance: 'faible' }, fichier: null }
            : q
        ));
      }
    });
  }, [codification]);

  const handleDragOver  = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = ()  => setDragOver(false);
  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    processFiles(e.dataTransfer.files);
  };
  const handleFileInput = (e) => {
    processFiles(e.target.files);
    e.target.value = '';
  };

  const handleConfirm = async (item, type, date, typeId) => {
    try {
      await axios.post('/api/documents', {
        soustraitant_codification: codification,
        type_document:     type || 'Document',
        type_document_id:  typeId || null,
        date_fin_validite: date || null,
        nom_fichier:       item.fichier?.nom || item.nom,
        chemin_fichier:    item.fichier?.chemin || '',
      });
      setQueue(prev => prev.filter(q => q.id !== item.id));

      // Si le fichier vient d'un email, le retirer de la liste des fichiers reçus
      if (item._fromEmail && item._emailChemin) {
        try {
          await axios.delete(`/api/soustraitants/${codification}/fichier-recu`, {
            data: { chemin: item._emailChemin, demande_id: item._emailDemandeId }
          });
          setFichiersRecus(prev => prev.filter(f => f.chemin !== item._emailChemin));
        } catch(e) { console.error('Erreur suppression fichier email:', e); }
      }

      await fetchDocuments();
      await fetchDetail();
      await fetchFichiersRecus();
    } catch(err) {
      alert(err.response?.data?.error || 'Erreur lors de l\'enregistrement.');
    }
  };

  const handleDiscard = (id) => {
    setQueue(prev => prev.filter(q => q.id !== id));
  };

  const fetchFichiersRecus = async () => {
    try {
      const r = await axios.get(`/api/soustraitants/${codification}/fichiers-recus`);
      setFichiersRecus(r.data);
    } catch(e) { console.error('Erreur fichiers reçus:', e); }
  };

  const handleAnalyserFichierRecu = async (fichier) => {
    setAnalyseEnCours(prev => ({ ...prev, [fichier.chemin]: 'loading' }));
    try {
      // Envoyer le fichier existant à l'analyse IA via le même endpoint que le drag&drop
      const fd = new FormData();
      // Récupérer le fichier depuis le serveur et le renvoyer pour analyse
      const response = await fetch(`/api/soustraitants/${codification}/analyser-fichier-recu`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': axios.defaults.headers.common['Authorization'] },
        body: JSON.stringify({ chemin: fichier.chemin }),
      });
      const analyse = await response.json();

      // Ajouter dans la queue d'analyse (comme le drag&drop)
      const newItem = {
        id: ++counterRef.current,
        nom: fichier.nom,
        statut: 'ready',
        analyse: analyse,
        fichier: analyse.fichier || { nom: fichier.nom, chemin: fichier.chemin },
        _fromEmail: true,
        _emailChemin: fichier.chemin,
        _emailDemandeId: fichier.demande_id,
      };
      setQueue(prev => [...prev, newItem]);
      setAnalyseEnCours(prev => ({ ...prev, [fichier.chemin]: 'done' }));
    } catch (err) {
      console.error('Erreur analyse:', err);
      setAnalyseEnCours(prev => ({ ...prev, [fichier.chemin]: 'error' }));
      alert('Erreur lors de l\'analyse');
    }
  };

  const handleAnalyserTous = async () => {
    const aTraiter = fichiersRecus.filter(f => analyseEnCours[f.chemin] !== 'done' && analyseEnCours[f.chemin] !== 'loading');
    for (const f of aTraiter) {
      await handleAnalyserFichierRecu(f);
    }
  };

  const handleSupprimerFichierRecu = async (fichier) => {
    try {
      await axios.delete(`/api/soustraitants/${codification}/fichier-recu`, {
        data: { chemin: fichier.chemin, demande_id: fichier.demande_id }
      });
      setFichiersRecus(prev => prev.filter(f => f.chemin !== fichier.chemin));
    } catch(err) {
      alert('Erreur');
    }
  };

  const handleDemandeDocuments = async () => {
    if (!window.confirm(`Envoyer une demande de documents à ${soustraitant.nom} (${soustraitant.mail}) ?`)) return;
    try {
      const response = await axios.post(`/api/soustraitants/${codification}/demande-documents`);
      alert(response.data.message);
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur lors de l\'envoi');
    }
  };

  const [editingDocId, setEditingDocId] = useState(null);
  const [editDocType, setEditDocType] = useState('');
  const [editDocDate, setEditDocDate] = useState('');

  const openEditDoc = (doc) => {
    setEditingDocId(doc.id);
    setEditDocType(doc.type || '');
    setEditDocDate(doc.date_fin_validite ? doc.date_fin_validite.split('T')[0] : '');
  };

  const cancelEditDoc = () => {
    setEditingDocId(null);
    setEditDocType('');
    setEditDocDate('');
  };

  const saveEditDoc = async (id) => {
    try {
      await axios.put(`/api/documents/${id}`, {
        type_document: editDocType || null,
        date_fin_validite: editDocDate || null,
      });
      setEditingDocId(null);
      await fetchDocuments();
      await fetchDetail();
    } catch(err) {
      alert(err.response?.data?.error || 'Erreur lors de la modification.');
    }
  };

  const handleDeleteDoc = async (id) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      await axios.delete(`/api/documents/${id}`);
      setDocuments(prev => prev.filter(d => d.id !== id));
    } catch(err) {
      alert(err.response?.data?.error || 'Erreur.');
    }
  };

  if (loading) return <div className="loading">Chargement…</div>;
  if (!data)   return <div className="error-message">Sous-traitant non trouvé</div>;

  const soustraitant = data;
  const dc4 = data.dc4s || [];

  return (
    <div>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
        .doc-row:hover { background: #f8f9fa; }
        .doc-row td { padding: 0.75rem 1rem; border-bottom: 1px solid #f0f0f0; vertical-align: middle; }
      `}</style>

      <div className="card">
        <div className="card-header">
          <h1 className="card-title">{soustraitant.nom}</h1>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {soustraitant.mail && soustraitant.statut_documents !== 'Conforme' && (
              <button
                onClick={handleDemandeDocuments}
                style={{
                  padding: '0.4rem 0.8rem', fontSize: '0.82rem', fontWeight: 600,
                  background: '#e67e22', color: '#fff', border: 'none', borderRadius: '4px',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem'
                }}
                title="Envoyer une demande de documents par email"
              >
                ✉ Demande docs
              </button>
            )}
            <button
              onClick={async () => {
                const newStatut = soustraitant.statut === 'Actif' ? 'Inactif' : 'Actif';
                if (window.confirm(`Passer ce sous-traitant en "${newStatut}" ?`)) {
                  try {
                    await axios.put(`/api/soustraitants/${codification}`, { statut: newStatut });
                    await fetchDetail();
                  } catch (err) {
                    alert('Erreur lors du changement de statut');
                  }
                }
              }}
              className={`badge ${soustraitant.statut === 'Actif' ? 'badge-success' : 'badge-warning'}`}
              style={{ cursor: 'pointer', border: 'none', fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}
              title={`Cliquer pour passer en ${soustraitant.statut === 'Actif' ? 'Inactif' : 'Actif'}`}
            >
              {soustraitant.statut === 'Actif' ? '🟢' : '🔴'} {soustraitant.statut}
            </button>
            {soustraitant.statut_documents && (
              <span className={`badge ${
                soustraitant.statut_documents === 'Conforme' ? 'badge-success' : 'badge-danger'
              }`}>
                {soustraitant.statut_documents === 'Conforme' ? '✓ Documents conformes' : '⚠ Documents non conformes'}
              </span>
            )}
          </div>
        </div>

        {/* Alerte documents manquants/expirés */}
        {soustraitant.statut_documents === 'Non conforme' && (
          <div style={{
            background: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '6px',
            padding: '1rem 1.2rem',
            margin: '1rem 1.5rem 0',
            fontSize: '0.9rem'
          }}>
            <div style={{ fontWeight: 700, color: '#856404', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '1.2rem' }}>⚠️</span>
              Documents manquants ou expirés
            </div>
            {soustraitant.documents_manquants && soustraitant.documents_manquants.length > 0 && (
              <div style={{ marginTop: '0.5rem' }}>
                <strong style={{ color: '#856404' }}>Manquants :</strong>
                <ul style={{ margin: '0.3rem 0 0', paddingLeft: '1.5rem', color: '#856404' }}>
                  {soustraitant.documents_manquants.map((doc, i) => (
                    <li key={i}>{doc}</li>
                  ))}
                </ul>
              </div>
            )}
            {soustraitant.documents_expires && soustraitant.documents_expires.length > 0 && (
              <div style={{ marginTop: '0.5rem' }}>
                <strong style={{ color: '#856404' }}>Expirés :</strong>
                <ul style={{ margin: '0.3rem 0 0', paddingLeft: '1.5rem', color: '#856404' }}>
                  {soustraitant.documents_expires.map((doc, i) => (
                    <li key={i}>{doc}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Infos générales avec édition inline */}
        <div className="detail-section">
          <h3>Informations générales</h3>
          <div className="detail-grid">
            <EditableField 
              label="Codification" 
              value={soustraitant.codification} 
              field="codification"
              disabled={true}
              onSave={handleFieldSave}
            />
            <EditableField 
              label="Nom" 
              value={soustraitant.nom} 
              field="nom"
              onSave={handleFieldSave}
            />
            <EditableField 
              label="SIRET" 
              value={soustraitant.siret} 
              field="siret"
              onSave={handleFieldSave}
            />
            <EditableField 
              label="Forme juridique" 
              value={soustraitant.forme_juridique} 
              field="forme_juridique"
              onSave={handleFieldSave}
            />
            <EditableField 
              label="Adresse" 
              value={soustraitant.adresse} 
              field="adresse"
              onSave={handleFieldSave}
            />
            <EditableField 
              label="Code postal" 
              value={soustraitant.code_postal} 
              field="code_postal"
              onSave={handleFieldSave}
            />
            <EditableField 
              label="Ville" 
              value={soustraitant.ville} 
              field="ville"
              onSave={handleFieldSave}
            />
            <EditableField 
              label="Représentant" 
              value={soustraitant.representant} 
              field="representant"
              onSave={handleFieldSave}
            />
            <EditableField 
              label="Titre représentant" 
              value={soustraitant.titre_representant} 
              field="titre_representant"
              onSave={handleFieldSave}
            />
            <EditableField 
              label="Email" 
              value={soustraitant.mail} 
              field="mail"
              onSave={handleFieldSave}
            />
            <EditableField 
              label="Téléphone" 
              value={soustraitant.telephone} 
              field="telephone"
              onSave={handleFieldSave}
            />
            <ActivitesField
              activites={soustraitant.activites}
              typesActivite={typesActivite}
              onSave={handleActivitesSave}
            />
            <EditableField 
              label="Nom établissement bancaire" 
              value={soustraitant.nom_banque} 
              field="nom_banque"
              onSave={handleFieldSave}
            />
            <EditableField 
              label="IBAN" 
              value={soustraitant.iban} 
              field="iban"
              onSave={handleFieldSave}
            />
            <EditableField 
              label="BIC" 
              value={soustraitant.bic} 
              field="bic"
              onSave={handleFieldSave}
            />
            <div className="detail-item">
              <div className="detail-label">PME / TPE</div>
              <div className="detail-value">
                <select
                  value={soustraitant.is_pme === true ? 'oui' : soustraitant.is_pme === false ? 'non' : ''}
                  onChange={async (e) => {
                    const val = e.target.value === 'oui' ? true : e.target.value === 'non' ? false : null;
                    await handleFieldSave('is_pme', val);
                  }}
                  style={{ padding: '0.4rem 0.6rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.9rem' }}
                >
                  <option value="">-- Non renseigné --</option>
                  <option value="oui">Oui</option>
                  <option value="non">Non</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Section Documents reçus par email */}
        {fichiersRecus.length > 0 && (
          <div className="detail-section" style={{ ...S.section, borderLeft: '4px solid #e67e22', background: '#fef9f4', borderRadius: '6px', padding: '1rem 1.5rem', margin: '1rem 1.5rem' }}>
            <div style={S.sectionHeader}>
              <h3 style={{ ...S.sectionTitle, color: '#e67e22' }}>
                📨 Documents reçus par email
                <span style={{ marginLeft: '0.5rem', background: '#e67e22', color: '#fff',
                  borderRadius: '10px', padding: '0.1rem 0.5rem', fontSize: '0.78rem', fontWeight: 600 }}>
                  {fichiersRecus.filter(f => analyseEnCours[f.chemin] !== 'done').length} à traiter
                </span>
              </h3>
              <button
                onClick={handleAnalyserTous}
                disabled={fichiersRecus.every(f => analyseEnCours[f.chemin] === 'done' || analyseEnCours[f.chemin] === 'loading')}
                style={{
                  padding: '0.4rem 0.9rem', fontSize: '0.82rem', fontWeight: 600,
                  background: '#27ae60', color: '#fff', border: 'none', borderRadius: '4px',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem',
                  opacity: fichiersRecus.every(f => analyseEnCours[f.chemin] === 'done' || analyseEnCours[f.chemin] === 'loading') ? 0.5 : 1,
                }}
                title="Analyser tous les fichiers avec l'IA">
                🤖 Tout analyser
              </button>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'rgba(230,126,34,0.1)' }}>
                  <th style={{ padding: '0.55rem 0.8rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: '#95a5a6' }}>Fichier</th>
                  <th style={{ padding: '0.55rem 0.8rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: '#95a5a6' }}>Référence</th>
                  <th style={{ padding: '0.55rem 0.8rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: '#95a5a6' }}>Reçu le</th>
                  <th style={{ padding: '0.55rem 0.8rem' }}></th>
                </tr>
              </thead>
              <tbody>
                {fichiersRecus.map((f, i) => {
                  const status = analyseEnCours[f.chemin];
                  const ext = f.nom.split('.').pop()?.toLowerCase();
                  const icon = ext === 'pdf' ? '📄' : '🖼️';
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid rgba(230,126,34,0.15)' }}>
                      <td style={{ padding: '0.6rem 0.8rem' }}>
                        <a href={`/api/uploads/${f.chemin.replace(/^uploads\//, '')}`} target="_blank" rel="noopener noreferrer"
                          style={{ color: '#e67e22', textDecoration: 'none', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                          <span>{icon}</span> {f.nom}
                        </a>
                      </td>
                      <td style={{ padding: '0.6rem 0.8rem', fontSize: '0.8rem', color: '#7f8c8d' }}>{f.code_suivi}</td>
                      <td style={{ padding: '0.6rem 0.8rem', fontSize: '0.8rem', color: '#7f8c8d' }}>
                        {f.date_reception ? new Date(f.date_reception).toLocaleDateString('fr-FR') : '—'}
                      </td>
                      <td style={{ padding: '0.6rem 0.8rem', textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
                          {status === 'done' ? (
                            <span style={{ fontSize: '0.8rem', color: '#27ae60', fontWeight: 600 }}>✓ En analyse</span>
                          ) : status === 'loading' ? (
                            <span style={S.spinner} />
                          ) : (
                            <>
                              <button
                                onClick={() => handleAnalyserFichierRecu(f)}
                                style={{ ...S.btnSmall('primary'), fontSize: '0.78rem', padding: '0.3rem 0.7rem' }}
                                title="Analyser avec l'IA">
                                🤖 Analyser
                              </button>
                              <button
                                onClick={() => handleSupprimerFichierRecu(f)}
                                style={{ ...S.btnSmall('danger'), fontSize: '0.78rem', padding: '0.3rem 0.7rem' }}
                                title="Supprimer">
                                S
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* 4) DC4 associés */}
        <div className="detail-section">
          <h3>DC4 associés ({dc4?.length || 0})</h3>
          {!dc4 || dc4.length === 0 ? (
            <p style={{ color: '#95a5a6' }}>Aucun DC4</p>
          ) : (
            <table>
              <thead>
                <tr><th>Numéro DC4</th><th>Marché</th><th>Montant</th><th>Statut</th><th></th></tr>
              </thead>
              <tbody>
                {dc4.map(d => (
                  <tr key={d.id}>
                    <td>{d.numero_dc4}</td>
                    <td>{d.intitule_marche}</td>
                    <td>{d.montant_previsionnel ? `${d.montant_previsionnel} €` : '—'}</td>
                    <td><span className="badge badge-info">{d.statut}</span></td>
                    <td><a href={`/dc4/${d.id}`} className="btn btn-primary">Voir</a></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* 5) Section Documents : tableau docs → queue IA → drop zone */}
        <div className="detail-section" style={S.section}>
          <div style={S.sectionHeader}>
            <h3 style={S.sectionTitle}>
              Documents
              <span style={{ marginLeft: '0.5rem', background: '#ecf0f1', color: '#7f8c8d',
                borderRadius: '10px', padding: '0.1rem 0.5rem', fontSize: '0.78rem', fontWeight: 600 }}>
                {documents.length}
              </span>
            </h3>
          </div>

          {/* 5a) Tableau des documents enregistrés */}
          {documents.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '1.5rem' }}>
              <thead>
                <tr style={{ background: '#f8f9fa' }}>
                  {['Type', 'Fichier', 'Fin de validité', 'Statut', ''].map(h => (
                    <th key={h} style={{ padding: '0.65rem 1rem', textAlign: 'left',
                      fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase',
                      letterSpacing: '0.05em', color: '#95a5a6', borderBottom: '2px solid #ecf0f1' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {documents.map(doc => {
                  const st = docStatut(doc.date_fin_validite);
                  const ext = doc.nom_fichier?.split('.').pop()?.toLowerCase();
                  const icon = ext === 'pdf' ? '📄' : '🖼️';
                  const isEditing = editingDocId === doc.id;
                  return (
                    <tr key={doc.id} className="doc-row">
                      <td style={{ fontWeight: 600, color: '#2c3e50', fontSize: '0.88rem' }}>
                        {isEditing ? (
                          <input type="text" value={editDocType}
                            onChange={e => setEditDocType(e.target.value)}
                            placeholder="Type de document"
                            style={{ ...S.fieldInput, border: '2px solid #3498db', fontWeight: 600 }}
                            autoFocus
                            onKeyDown={e => {
                              if (e.key === 'Enter') saveEditDoc(doc.id);
                              if (e.key === 'Escape') cancelEditDoc();
                            }}
                          />
                        ) : (
                          doc.type || <em style={{ color: '#aaa', fontWeight: 400 }}>Non renseigné</em>
                        )}
                      </td>
                      <td>
                        <a href={`/api/uploads/${(doc.chemin_fichier || '').replace(/^uploads\//, '')}`} target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#3498db', textDecoration: 'none', fontSize: '0.85rem', display: 'flex', alignItems: 'center' }}>
                          <span style={S.fileIcon}>{icon}</span>
                          {doc.nom_fichier}
                        </a>
                      </td>
                      <td style={{ color: '#5d6d7e', fontSize: '0.85rem' }}>
                        {isEditing ? (
                          <input type="date" value={editDocDate}
                            onChange={e => setEditDocDate(e.target.value)}
                            style={{ ...S.fieldInput, border: '2px solid #3498db' }}
                            onKeyDown={e => {
                              if (e.key === 'Enter') saveEditDoc(doc.id);
                              if (e.key === 'Escape') cancelEditDoc();
                            }}
                          />
                        ) : (
                          doc.date_fin_validite ? fmtDate(doc.date_fin_validite) : <em style={{ color: '#bbb' }}>—</em>
                        )}
                      </td>
                      <td><span style={S.badge(st.style)}>{st.label}</span></td>
                      <td style={{ textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
                          {isEditing ? (
                            <>
                              <button style={{ ...S.btnSmall('success'), fontSize: '0.78rem', padding: '0.25rem 0.6rem' }}
                                onClick={() => saveEditDoc(doc.id)} title="Enregistrer">
                                ✓
                              </button>
                              <button style={{ ...S.btnSmall('light'), fontSize: '0.78rem', padding: '0.25rem 0.6rem' }}
                                onClick={cancelEditDoc} title="Annuler">
                                ✕
                              </button>
                            </>
                          ) : (
                            <>
                              <button style={{ ...S.btnSmall('primary'), fontSize: '0.78rem', padding: '0.25rem 0.6rem' }}
                                onClick={() => openEditDoc(doc)} title="Modifier">
                                M
                              </button>
                              <button style={{ ...S.btnSmall('danger'), fontSize: '0.78rem', padding: '0.25rem 0.6rem' }}
                                onClick={() => handleDeleteDoc(doc.id)} title="Supprimer">
                                S
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* 5b) Queue d'analyse IA */}
          {queue.length > 0 && (
            <div style={{ marginBottom: '1.5rem', animation: 'fadeIn 0.3s ease' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: '0.07em', color: '#7f8c8d', marginBottom: '0.75rem' }}>
                🤖 Analyse IA — {queue.filter(q => q.statut === 'loading').length > 0
                  ? `${queue.filter(q=>q.statut==='loading').length} en cours…`
                  : 'Vérifiez et confirmez'}
              </div>
              {queue.map(item => (
                item.statut === 'loading'
                  ? <SpinnerIA key={item.id} nom={item.nom} />
                  : <CarteAnalyse key={item.id} item={item}
                      typesDocuments={typesDocuments}
                      onConfirm={handleConfirm} onDiscard={handleDiscard} />
              ))}
            </div>
          )}

          {/* 5c) Zone glisser-déposer */}
          <div
            style={S.dropZone(dragOver)}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div style={S.dropIcon}>
              {dragOver ? '📂' : '📁'}
            </div>
            <div style={S.dropText}>
              <span style={S.dropBold}>Glissez-déposez vos documents ici</span><br/>
              ou cliquez pour sélectionner des fichiers<br/>
              <span style={{ fontSize: '0.78rem', color: '#bdc3c7', marginTop: '0.35rem', display: 'block' }}>
                PDF, JPG, PNG, WEBP — max 10 Mo — <strong>Analyse IA automatique 🤖</strong>
              </span>
            </div>
          </div>
          <input ref={fileInputRef} type="file" multiple
            accept=".pdf,.jpg,.jpeg,.png,.webp"
            style={{ display: 'none' }} onChange={handleFileInput} />

          {documents.length === 0 && queue.length === 0 && (
            <div style={{ textAlign: 'center', padding: '1rem', color: '#bdc3c7', fontSize: '0.85rem' }}>
              Aucun document enregistré
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SoustraitantDetail;
